import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ArrowRight } from "lucide-react";

export const Eyebrow = ({ children, className = "" }) => (
    <div className={`eyebrow ${className}`}>{children}</div>
);

export const SectionTitle = ({ kicker, title, lead, align = "left", className = "" }) => (
    <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""} ${className}`}>
        {kicker && <Eyebrow className="mb-4">{kicker}</Eyebrow>}
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-medium text-white leading-[1.05] tracking-tight">{title}</h2>
        {lead && <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed max-w-2xl">{lead}</p>}
    </div>
);

export const Container = ({ children, className = "" }) => (
    <div className={`max-w-7xl mx-auto px-6 md:px-8 lg:px-12 ${className}`}>{children}</div>
);

export const Section = ({ children, className = "", id }) => (
    <section id={id} className={`relative py-20 md:py-28 ${className}`}>{children}</section>
);

export const Card = ({ children, className = "", hover = true, ...props }) => (
    <div
        className={`relative bg-ink-700/40 backdrop-blur-xl border border-white/10 p-8 transition-all duration-300 ${
            hover ? "hover:border-electric-400/50 hover:bg-ink-700/60 hover:-translate-y-1" : ""
        } ${className}`}
        {...props}
    >
        {children}
    </div>
);

export const Stat = ({ value, label, suffix = "" }) => (
    <div>
        <div className="font-heading font-medium text-3xl md:text-4xl lg:text-5xl text-electric-400 tracking-tight">{value}<span className="text-electric-400">{suffix}</span></div>
        <div className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">{label}</div>
    </div>
);

export const PageHero = ({ kicker, title, lead, image, children, height = "h-[55vh] min-h-[420px]" }) => (
    <section className={`relative ${height} flex items-end overflow-hidden`} data-testid="page-hero">
        {image && (
            <div className="absolute inset-0">
                <img src={image} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 hero-overlay" />
                <div className="absolute inset-0 hero-overlay-bottom" />
            </div>
        )}
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-8 lg:px-12 pb-16 md:pb-20 w-full">
            <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                className="max-w-3xl"
            >
                {kicker && <Eyebrow className="mb-5">{kicker}</Eyebrow>}
                <h1 className="font-heading font-medium text-white text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">{title}</h1>
                {lead && <p className="mt-6 text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">{lead}</p>}
                {children && <div className="mt-8 flex flex-wrap gap-3">{children}</div>}
            </motion.div>
        </div>
    </section>
);

export const Breadcrumbs = ({ items }) => (
    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 flex items-center gap-2 flex-wrap">
        {items.map((it, i) => (
            <React.Fragment key={i}>
                {it.to ? <Link to={it.to} className="hover:text-electric-400">{it.label}</Link> : <span className="text-electric-400">{it.label}</span>}
                {i < items.length - 1 && <ChevronRight className="w-3 h-3" />}
            </React.Fragment>
        ))}
    </div>
);

export const CTABand = ({ title, subtitle, primaryTo = "/contact", primaryLabel = "Request Consultation", secondaryTo = "/site-host", secondaryLabel = "Become a Site Host" }) => (
    <section className="relative py-20 border-y border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-electric-400/5 via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-8 lg:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
                <h3 className="text-2xl md:text-4xl font-heading font-medium text-white tracking-tight max-w-xl">{title}</h3>
                {subtitle && <p className="mt-3 text-slate-400 max-w-lg">{subtitle}</p>}
            </div>
            <div className="flex flex-wrap gap-3">
                <Link to={primaryTo} data-testid="ctaband-primary" className="btn-primary">{primaryLabel} <ArrowRight className="w-4 h-4" /></Link>
                <Link to={secondaryTo} data-testid="ctaband-secondary" className="btn-secondary">{secondaryLabel}</Link>
            </div>
        </div>
    </section>
);
